import { lazy, Suspense } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { AuthProvider } from './auth/AuthContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { RequirePermission } from './components/RequirePermission';
import { Layout } from './components/Layout';
import { LoginPage } from './pages/LoginPage';
import { AuthCallbackPage } from './pages/AuthCallbackPage';
import { NotFoundPage } from './pages/NotFoundPage';

const DashboardPage        = lazy(() => import('./pages/DashboardPage').then(m => ({ default: m.DashboardPage })));
const FlowsLayout          = lazy(() => import('./pages/flows/FlowsLayout').then(m => ({ default: m.FlowsLayout })));
const FlowsEmpty           = lazy(() => import('./pages/flows/FlowsEmpty').then(m => ({ default: m.FlowsEmpty })));
const FlowDetailPanel      = lazy(() => import('./pages/flows/FlowDetailPanel').then(m => ({ default: m.FlowDetailPanel })));
const DeploymentDetailPage = lazy(() => import('./pages/DeploymentDetailPage').then(m => ({ default: m.DeploymentDetailPage })));
const RunDetailPage        = lazy(() => import('./pages/runs/RunDetailPage').then(m => ({ default: m.RunDetailPage })));
const RunsPage             = lazy(() => import('./pages/runs/RunsPage').then(m => ({ default: m.RunsPage })));
const CreateDeploymentPage = lazy(() => import('./pages/deployments/CreateDeploymentPage').then(m => ({ default: m.CreateDeploymentPage })));
const UpcomingPage         = lazy(() => import('./pages/upcoming/UpcomingPage').then(m => ({ default: m.UpcomingPage })));
const WorkPoolsPage        = lazy(() => import('./pages/WorkPoolsPage').then(m => ({ default: m.WorkPoolsPage })));
const VariablesPage        = lazy(() => import('./pages/VariablesPage').then(m => ({ default: m.VariablesPage })));
const BlocksPage           = lazy(() => import('./pages/BlocksPage').then(m => ({ default: m.BlocksPage })));
const EntraConfigPage      = lazy(() => import('./pages/admin/EntraConfigPage').then(m => ({ default: m.EntraConfigPage })));
const UsersPage            = lazy(() => import('./pages/admin/UsersPage').then(m => ({ default: m.UsersPage })));
const FlowPermissionsPage  = lazy(() => import('./pages/admin/FlowPermissionsPage').then(m => ({ default: m.FlowPermissionsPage })));
const AuditViewerPage      = lazy(() => import('./pages/admin/AuditViewerPage').then(m => ({ default: m.AuditViewerPage })));
const ApiKeysPage          = lazy(() => import('./pages/ApiKeysPage').then(m => ({ default: m.ApiKeysPage })));

export function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Suspense>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/auth/callback" element={<AuthCallbackPage />} />
            <Route
              element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }
            >
              <Route index element={<DashboardPage />} />
              <Route path="flows" element={<FlowsLayout />}>
                <Route index element={<FlowsEmpty />} />
                <Route path=":flowId" element={<FlowDetailPanel />} />
              </Route>
              <Route path="deployments/:deploymentId" element={<DeploymentDetailPage />} />
              <Route path="execucoes" element={<RunsPage />} />
              <Route path="runs/:runId" element={<RunDetailPage />} />
              <Route path="deployments/new" element={<RequirePermission permission="infra.manage"><CreateDeploymentPage /></RequirePermission>} />
              <Route path="agendamentos" element={<UpcomingPage />} />
              <Route path="work-pools" element={<RequirePermission permission="infra.manage"><WorkPoolsPage /></RequirePermission>} />
              <Route path="variables" element={<RequirePermission permission="config.manage"><VariablesPage /></RequirePermission>} />
              <Route path="blocks" element={<RequirePermission permission="config.manage"><BlocksPage /></RequirePermission>} />
              <Route path="admin/entra" element={<RequirePermission permission="admin.users.manage"><EntraConfigPage /></RequirePermission>} />
              <Route path="admin/users" element={<RequirePermission permission="admin.users.manage"><UsersPage /></RequirePermission>} />
              <Route path="admin/flow-permissions" element={<RequirePermission permission="admin.flows.permissions.manage"><FlowPermissionsPage /></RequirePermission>} />
              <Route path="admin/audit" element={<RequirePermission permission="admin.users.manage"><AuditViewerPage /></RequirePermission>} />
              <Route path="api-keys" element={<ApiKeysPage />} />
              <Route path="*" element={<NotFoundPage />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </AuthProvider>
    </BrowserRouter>
  );
}
